import { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { fmtUSD, fmtNum, fmtPct, fmtDate, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Applications as AdminApplications, Clients as AdminClients } from "./AdminPortal";

const Tile = ({ label, value, testid }) => (
  <div className="stat-tile" data-testid={testid}><div className="kicker mb-2">{label}</div><div className="font-mono text-2xl">{value}</div></div>
);
const F = ({ label, children }) => (<label className="block"><span className="kicker text-[10px] block mb-2">{label}</span>{children}</label>);

function Metrics() {
  const [m, setM] = useState(null);
  const [resetInfo, setResetInfo] = useState({ used: false });
  const [resetting, setResetting] = useState(false);
  const [funds, setFunds] = useState([]);
  const [editing, setEditing] = useState(null); // fund_id being edited
  const [editDraft, setEditDraft] = useState({});
  const [creating, setCreating] = useState(false);
  const [newFund, setNewFund] = useState({
    id: "", code: "", name: "", tagline: "", description: "",
    instruments: "", inception_date: new Date().toISOString().slice(0, 10),
    status: "active", initial_nav: 1000, target_aum: 0,
  });
  const loadFunds = () => api.get("/funds").then((r) => setFunds(r.data));
  const load = () => Promise.all([
    api.get("/super/metrics").then((r) => setM(r.data)),
    api.get("/super/reset-status").then((r) => setResetInfo(r.data)),
    loadFunds(),
  ]);
  useEffect(() => { load(); }, []);
  const startEdit = (f) => {
    setEditing(f.id);
    setEditDraft({
      code: f.code || "", name: f.name || "", tagline: f.tagline || "",
      description: f.description || "",
      instruments: (f.instruments || []).join(", "),
      inception_date: (f.inception_date || "").slice(0, 10),
      status: f.status || "active",
      initial_nav: f.initial_nav || 1000,
      target_aum: f.target_aum || 0,
    });
  };
  const saveEdit = async () => {
    try {
      const payload = { ...editDraft,
        instruments: editDraft.instruments.split(",").map(s => s.trim()).filter(Boolean),
        initial_nav: Number(editDraft.initial_nav),
        target_aum: Number(editDraft.target_aum),
      };
      await api.patch(`/super/funds/${editing}`, payload);
      toast.success(`Fund ${editing} updated.`);
      setEditing(null); setEditDraft({});
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const createFund = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newFund,
        id: newFund.id.trim().toLowerCase().replace(/\s+/g, "-"),
        instruments: newFund.instruments.split(",").map(s => s.trim()).filter(Boolean),
        initial_nav: Number(newFund.initial_nav),
        target_aum: Number(newFund.target_aum),
      };
      await api.post("/super/funds", payload);
      toast.success(`Fund ${payload.id} created.`);
      setCreating(false);
      setNewFund({ id: "", code: "", name: "", tagline: "", description: "",
        instruments: "", inception_date: new Date().toISOString().slice(0, 10),
        status: "active", initial_nav: 1000, target_aum: 0 });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const deleteFund = async (f) => {
    const confirm1 = window.prompt(`DELETE fund "${f.name}" (id: ${f.id}).\nThis wipes ALL holdings, transactions, NAV snapshots, fee schedules, requests, trades, and MT5 config for this fund.\n\nType the fund id (${f.id}) to confirm.`);
    if (confirm1 !== f.id) return;
    try {
      const r = await api.delete(`/super/funds/${f.id}`);
      toast.success(`Fund ${f.id} deleted. Cleared: ${Object.entries(r.data.cleared).map(([k,v])=>`${k}:${v}`).join(" · ")}`);
      load();
    } catch (e) {
      // Handle 409 (positive holdings) with a force option
      const msg = formatErr(e);
      if (String(msg).toLowerCase().includes("holding") && window.confirm(`${msg}\n\nHard-delete anyway? This DESTROYS all client holdings for this fund.`)) {
        try {
          const r = await api.delete(`/super/funds/${f.id}?force=true`);
          toast.success(`Fund ${f.id} force-deleted.`);
          load();
        } catch (e2) { toast.error(formatErr(e2)); }
      } else { toast.error(msg); }
    }
  };
  const doReset = async () => {
    const s = window.prompt('RESET DEMO DATA. This wipes applications, requests, holdings, transactions, corporate ledger, e-signatures, Stripe intents, audit-log, and the demo client account. Funds, fee schedules and staff accounts are preserved. Type RESET to confirm.');
    if (s !== "RESET") return;
    setResetting(true);
    try {
      const today = new Date().toISOString().slice(0, 10); // UTC yyyy-mm-dd, matches backend guard
      const r = await api.post("/super/reset-demo-data", null,
                                { headers: { "X-Reset-Confirm": today } });
      toast.success(`Go-live reset complete. ${Object.entries(r.data.cleared).map(([k, v]) => `${k}: ${v}`).join(" · ")}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setResetting(false); }
  };
  if (!m) return <div className="text-muted-foreground">Loading…</div>;
  return (
    <div>
      <h1 className="text-4xl mb-8">Business Metrics</h1>
      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <Tile label="Total AUM" value={fmtUSD(m.total_aum)} testid="s-aum" />
        <Tile label="Active Clients" value={m.active_clients} testid="s-clients" />
        <Tile label="Pending Applications" value={m.pending_applications} testid="s-apps" />
        <Tile label="Corporate Revenue" value={fmtUSD(m.corporate_revenue)} testid="s-rev" />
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-10 text-xs text-muted-foreground">
        <div className="border border-border p-4">
          <div className="kicker mb-1">Subscribed AUM (on-platform units × NAV)</div>
          <div className="font-mono text-lg text-foreground" data-testid="s-subscribed-aum">{fmtUSD(m.total_subscribed_aum || 0)}</div>
        </div>
        <div className="border border-border p-4">
          <div className="kicker mb-1">Pooled Equity (fund target + MT5 cumulative P/L)</div>
          <div className="font-mono text-lg text-foreground" data-testid="s-pooled-equity">{fmtUSD(m.total_pooled_equity || 0)}</div>
        </div>
      </div>
      <div className="kicker mb-3">Per Fund</div>
      <table className="w-full text-sm mb-16">
        <thead className="text-muted-foreground text-xs uppercase tracking-widest">
          <tr><th className="text-left pb-3">Fund</th><th className="text-right pb-3">NAV/Unit</th><th className="text-right pb-3">AUM</th><th className="text-right pb-3">Subscribed</th><th className="text-right pb-3">Pooled Equity</th><th className="text-right pb-3">Clients</th><th className="text-right pb-3">Annualized · Net</th></tr>
        </thead>
        <tbody>
          {m.per_fund.map((f) => (
            <tr key={f.fund_id} className="border-t border-border row-hover" data-testid={`s-fund-${f.fund_id}`}>
              <td className="py-3">{f.name}</td>
              <td className="py-3 text-right font-mono">{fmtNum(f.nav_per_unit, 4)}</td>
              <td className="py-3 text-right font-mono">{fmtUSD(f.aum)}</td>
              <td className="py-3 text-right font-mono text-muted-foreground">{fmtUSD(f.subscribed_aum || 0)}</td>
              <td className="py-3 text-right font-mono text-muted-foreground">{fmtUSD(f.pooled_equity || 0)}</td>
              <td className="py-3 text-right font-mono">{f.clients}</td>
              <td className={`py-3 text-right font-mono ${(f.annualized_net_pct ?? f.perf_since_inception_pct) >= 0 ? "pos" : "neg"}`}>{(f.annualized_net_pct ?? f.perf_since_inception_pct) >= 0 ? "+" : ""}{fmtPct(f.annualized_net_pct ?? f.perf_since_inception_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* -------- Fund Management (Super Admin control) -------- */}
      <div className="border border-border p-6 mb-10" data-testid="fund-management-panel">
        <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
          <div>
            <div className="kicker mb-1">Fund Management</div>
            <h3 className="text-xl">Funds Roster</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-xl">Create, edit, or delete funds without an agent round-trip. Edits persist across restarts.</p>
          </div>
          <button data-testid="fund-new-btn" onClick={() => setCreating((v) => !v)}
            className="btn-gold px-5 py-2 text-xs uppercase tracking-widest">
            {creating ? "Cancel" : "+ New Fund"}
          </button>
        </div>

        {creating && (
          <form onSubmit={createFund} className="surface p-5 mb-6 grid md:grid-cols-2 gap-4" data-testid="fund-create-form">
            <F label="Fund ID (slug, e.g. fx-crypto)"><input required value={newFund.id} onChange={(e) => setNewFund({...newFund, id: e.target.value})} className="fld font-mono" data-testid="fund-new-id" /></F>
            <F label="Code (e.g. FXC)"><input required value={newFund.code} onChange={(e) => setNewFund({...newFund, code: e.target.value})} className="fld" data-testid="fund-new-code" /></F>
            <F label="Name"><input required value={newFund.name} onChange={(e) => setNewFund({...newFund, name: e.target.value})} className="fld" data-testid="fund-new-name" /></F>
            <F label="Status">
              <select value={newFund.status} onChange={(e) => setNewFund({...newFund, status: e.target.value})} className="fld" data-testid="fund-new-status">
                <option value="active">Active</option><option value="paused">Paused</option>
                <option value="closed">Closed</option><option value="coming_soon">Coming Soon</option>
              </select>
            </F>
            <F label="Tagline"><input value={newFund.tagline} onChange={(e) => setNewFund({...newFund, tagline: e.target.value})} className="fld" data-testid="fund-new-tagline" /></F>
            <F label="Instruments (comma-separated)"><input value={newFund.instruments} onChange={(e) => setNewFund({...newFund, instruments: e.target.value})} placeholder="FX, Metals, Indices" className="fld" data-testid="fund-new-instruments" /></F>
            <F label="Inception Date"><input required type="date" value={newFund.inception_date} onChange={(e) => setNewFund({...newFund, inception_date: e.target.value})} className="fld font-mono" data-testid="fund-new-inception" /></F>
            <F label="Initial NAV (USD)"><input required type="number" step="0.01" value={newFund.initial_nav} onChange={(e) => setNewFund({...newFund, initial_nav: e.target.value})} className="fld font-mono" data-testid="fund-new-nav" /></F>
            <F label="Target AUM (USD)"><input type="number" step="1" value={newFund.target_aum} onChange={(e) => setNewFund({...newFund, target_aum: e.target.value})} className="fld font-mono" data-testid="fund-new-target" /></F>
            <div className="md:col-span-2">
              <F label="Description"><textarea rows={3} value={newFund.description} onChange={(e) => setNewFund({...newFund, description: e.target.value})} className="fld" data-testid="fund-new-description" /></F>
            </div>
            <div className="md:col-span-2">
              <button type="submit" data-testid="fund-new-submit" className="btn-gold px-6 py-2.5 text-xs uppercase tracking-widest">Create Fund</button>
            </div>
            <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}`}</style>
          </form>
        )}

        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left pb-3">ID</th>
              <th className="text-left pb-3">Code</th>
              <th className="text-left pb-3">Name</th>
              <th className="text-right pb-3">Initial NAV</th>
              <th className="text-right pb-3">Target AUM</th>
              <th className="text-left pb-3 pl-4">Status</th>
              <th className="text-right pb-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {funds.map((f) => (
              <tr key={f.id} className="border-t border-border row-hover" data-testid={`fund-row-${f.id}`}>
                <td className="py-3 font-mono text-xs">{f.id}</td>
                <td className="py-3 font-mono text-xs">{f.code}</td>
                <td className="py-3">{f.name}</td>
                <td className="py-3 text-right font-mono">{fmtNum(f.initial_nav, 2)}</td>
                <td className="py-3 text-right font-mono">{fmtUSD(f.target_aum || 0)}</td>
                <td className="py-3 pl-4 text-xs uppercase">{f.status}</td>
                <td className="py-3 text-right whitespace-nowrap">
                  <button data-testid={`fund-edit-${f.id}`} onClick={() => startEdit(f)}
                    className="text-xs uppercase tracking-widest border border-border px-3 py-1 mr-2 hover:bg-secondary">Edit</button>
                  <button data-testid={`fund-delete-${f.id}`} onClick={() => deleteFund(f)}
                    className="text-xs uppercase tracking-widest border border-destructive/60 text-destructive px-3 py-1 hover:bg-destructive hover:text-destructive-foreground">Delete</button>
                </td>
              </tr>
            ))}
            {funds.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-muted-foreground text-sm">No funds. Click "+ New Fund" to create one.</td></tr>
            )}
          </tbody>
        </table>

        {editing && (
          <div className="mt-6 border border-primary/40 bg-card/40 p-5" data-testid="fund-edit-form">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg">Edit Fund · <span className="font-mono text-sm gold">{editing}</span></h4>
              <button onClick={() => { setEditing(null); setEditDraft({}); }} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">Close</button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <F label="Code"><input value={editDraft.code} onChange={(e) => setEditDraft({...editDraft, code: e.target.value})} className="fld" data-testid="fund-edit-code" /></F>
              <F label="Name"><input value={editDraft.name} onChange={(e) => setEditDraft({...editDraft, name: e.target.value})} className="fld" data-testid="fund-edit-name" /></F>
              <F label="Status">
                <select value={editDraft.status} onChange={(e) => setEditDraft({...editDraft, status: e.target.value})} className="fld" data-testid="fund-edit-status">
                  <option value="active">Active</option><option value="paused">Paused</option>
                  <option value="closed">Closed</option><option value="coming_soon">Coming Soon</option>
                </select>
              </F>
              <F label="Inception Date"><input type="date" value={editDraft.inception_date} onChange={(e) => setEditDraft({...editDraft, inception_date: e.target.value})} className="fld font-mono" data-testid="fund-edit-inception" /></F>
              <F label="Tagline"><input value={editDraft.tagline} onChange={(e) => setEditDraft({...editDraft, tagline: e.target.value})} className="fld" data-testid="fund-edit-tagline" /></F>
              <F label="Instruments (comma-separated)"><input value={editDraft.instruments} onChange={(e) => setEditDraft({...editDraft, instruments: e.target.value})} className="fld" data-testid="fund-edit-instruments" /></F>
              <F label="Initial NAV (USD)"><input type="number" step="0.01" value={editDraft.initial_nav} onChange={(e) => setEditDraft({...editDraft, initial_nav: e.target.value})} className="fld font-mono" data-testid="fund-edit-nav" /></F>
              <F label="Target AUM (USD)"><input type="number" step="1" value={editDraft.target_aum} onChange={(e) => setEditDraft({...editDraft, target_aum: e.target.value})} className="fld font-mono" data-testid="fund-edit-target" /></F>
              <div className="md:col-span-2">
                <F label="Description"><textarea rows={3} value={editDraft.description} onChange={(e) => setEditDraft({...editDraft, description: e.target.value})} className="fld" data-testid="fund-edit-description" /></F>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button data-testid="fund-edit-save" onClick={saveEdit} className="btn-gold px-6 py-2.5 text-xs uppercase tracking-widest">Save Changes</button>
              <button onClick={() => { setEditing(null); setEditDraft({}); }} className="border border-border px-6 py-2.5 text-xs uppercase tracking-widest">Cancel</button>
            </div>
            <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}`}</style>
          </div>
        )}
      </div>

      <div className="border border-destructive/40 bg-destructive/5 p-6">
        <div className="kicker mb-2 text-destructive">Reset Demo Data (Testing Mode)</div>
        <h3 className="text-xl mb-2">Reset Demo Data</h3>
        <p className="text-sm text-muted-foreground mb-2 max-w-2xl leading-relaxed">
          Wipes every application, request, holding, transaction, corporate-ledger entry, e-signature, Stripe intent, audit-log line, and removes the demo client account. Preserves the funds, fee schedules, and the staff accounts (<span className="font-mono">maqqqus</span>, <span className="font-mono">superadmin</span>, <span className="font-mono">staff</span>).
        </p>
        {resetInfo.used && (
          <p className="text-xs text-muted-foreground mb-4">Last reset: <span className="font-mono">{fmtDateTime(resetInfo.detail?.used_at)}</span> by <span className="font-mono gold">{resetInfo.detail?.used_by}</span></p>
        )}
        <button data-testid="reset-demo-btn" onClick={doReset} disabled={resetting}
          className="px-6 py-2.5 text-xs uppercase tracking-widest disabled:opacity-50 border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground">
          {resetting ? "Resetting…" : "Reset Demo Data"}
        </button>
      </div>
    </div>
  );
}

function NAVEntry() {
  const [funds, setFunds] = useState([]);
  const [snaps, setSnaps] = useState([]);
  const [f, setF] = useState({ fund_id: "", total_pooled_equity: 0, note: "" });
  const loadSnaps = (fund_id) => { if (fund_id) api.get(`/funds/${fund_id}/nav-history`).then((r) => setSnaps(r.data.reverse().slice(0, 30))); };
  useEffect(() => { api.get("/funds").then((r) => { setFunds(r.data); setF((x) => ({ ...x, fund_id: r.data[0]?.id || "" })); loadSnaps(r.data[0]?.id); }); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/super/nav", { fund_id: f.fund_id, total_pooled_equity: Number(f.total_pooled_equity), note: f.note }); toast.success(`NAV posted: ${fmtNum(r.data.nav_per_unit, 4)}`); loadSnaps(f.fund_id); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div>
        <h1 className="text-3xl mb-2">Manual NAV Entry</h1>
        <p className="text-sm text-muted-foreground mb-6">Post an end-of-day pooled equity value per fund. NAV per unit is derived from total units outstanding.</p>
        <form onSubmit={submit} className="surface p-6 space-y-4">
          <F label="Fund"><select value={f.fund_id} onChange={(e) => { setF({...f, fund_id: e.target.value}); loadSnaps(e.target.value); }} className="fld" data-testid="nav-fund">{funds.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></F>
          <F label="Total pooled equity (USD)"><input required type="number" step="0.01" value={f.total_pooled_equity} onChange={(e) => setF({...f, total_pooled_equity: e.target.value})} className="fld font-mono" data-testid="nav-equity" /></F>
          <F label="Note (optional)"><input value={f.note} onChange={(e) => setF({...f, note: e.target.value})} className="fld" data-testid="nav-note" /></F>
          <button data-testid="nav-submit" className="btn-gold w-full py-3 text-xs uppercase tracking-widest">Post Snapshot</button>
          <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}`}</style>
        </form>
      </div>
      <div>
        <h2 className="text-xl mb-4">Recent Snapshots</h2>
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs uppercase tracking-widest"><tr><th className="text-left pb-3">Time</th><th className="text-right pb-3">Equity</th><th className="text-right pb-3">Units</th><th className="text-right pb-3">NAV/Unit</th></tr></thead>
          <tbody>{snaps.map((s) => (
            <tr key={s.id} className="border-t border-border">
              <td className="py-2 font-mono text-xs">{fmtDateTime(s.timestamp)}</td>
              <td className="py-2 text-right font-mono">{fmtUSD(s.total_pooled_equity)}</td>
              <td className="py-2 text-right font-mono">{fmtNum(s.total_units_outstanding, 2)}</td>
              <td className="py-2 text-right font-mono">{fmtNum(s.nav_per_unit, 4)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function Fees() {
  const [funds, setFunds] = useState([]);
  const [sched, setSched] = useState([]);
  const [f, setF] = useState({ fund_id: "", performance_fee_pct: 50, management_fee_pct: 0, crystallization_frequency: "quarterly" });
  const load = () => api.get("/super/fee-schedules").then((r) => setSched(r.data));
  useEffect(() => { api.get("/funds").then((r) => { setFunds(r.data); setF((x) => ({ ...x, fund_id: r.data[0]?.id || "" })); }); load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/super/fee-schedules", f); toast.success("Fee schedule updated."); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div>
        <h1 className="text-3xl mb-6">Fee Schedule</h1>
        <form onSubmit={submit} className="surface p-6 space-y-4">
          <F label="Fund"><select value={f.fund_id} onChange={(e) => setF({...f, fund_id: e.target.value})} className="fld" data-testid="fee-fund">{funds.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></F>
          <F label="Performance fee %"><input required type="number" step="0.01" value={f.performance_fee_pct} onChange={(e) => setF({...f, performance_fee_pct: Number(e.target.value)})} className="fld font-mono" data-testid="fee-perf" /></F>
          <F label="Management fee %"><input required type="number" step="0.01" value={f.management_fee_pct} onChange={(e) => setF({...f, management_fee_pct: Number(e.target.value)})} className="fld font-mono" data-testid="fee-mgmt" /></F>
          <F label="Crystallization"><select value={f.crystallization_frequency} onChange={(e) => setF({...f, crystallization_frequency: e.target.value})} className="fld" data-testid="fee-freq">
            <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="on_redemption">On Redemption Only</option>
          </select></F>
          <button data-testid="fee-submit" className="btn-gold w-full py-3 text-xs uppercase tracking-widest">Save Version</button>
          <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}`}</style>
        </form>
      </div>
      <div>
        <h2 className="text-xl mb-4">Version History</h2>
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs uppercase tracking-widest"><tr><th className="text-left pb-3">Fund</th><th className="text-right pb-3">Perf %</th><th className="text-right pb-3">Mgmt %</th><th className="text-left pb-3 pl-4">Freq</th><th className="text-left pb-3 pl-4">Effective From</th></tr></thead>
          <tbody>{sched.map((s) => (
            <tr key={s.id} className={`border-t border-border ${s.effective_to ? "text-muted-foreground" : ""}`}>
              <td className="py-2 font-mono text-xs">{s.fund_id}</td>
              <td className="py-2 text-right font-mono">{fmtPct(s.performance_fee_pct)}</td>
              <td className="py-2 text-right font-mono">{fmtPct(s.management_fee_pct)}</td>
              <td className="py-2 pl-4 text-xs">{s.crystallization_frequency}</td>
              <td className="py-2 pl-4 font-mono text-xs">{fmtDateTime(s.effective_from)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function Corporate() {
  const [data, setData] = useState({ entries: [], balance: 0 });
  useEffect(() => { api.get("/super/corporate-ledger").then((r) => setData(r.data)); }, []);
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl">Corporate Ledger</h1>
        <div className="text-right"><div className="kicker mb-1">Balance</div><div className="font-mono text-2xl gold">{fmtUSD(data.balance)}</div></div>
      </div>
      <table className="w-full text-sm">
        <thead className="text-muted-foreground text-xs uppercase tracking-widest"><tr><th className="text-left pb-3">Time</th><th className="text-left pb-3">Type</th><th className="text-left pb-3">Fund</th><th className="text-left pb-3">Description</th><th className="text-right pb-3">Amount</th></tr></thead>
        <tbody>{data.entries.map((e) => (
          <tr key={e.id} className="border-t border-border row-hover">
            <td className="py-3 font-mono text-xs">{fmtDateTime(e.timestamp)}</td>
            <td className="py-3 uppercase text-xs">{e.type}</td>
            <td className="py-3 font-mono text-xs">{e.fund_id}</td>
            <td className="py-3 text-xs text-muted-foreground">{e.description}</td>
            <td className="py-3 text-right font-mono pos">{fmtUSD(e.amount)}</td>
          </tr>
        ))}</tbody>
      </table>
      {data.entries.length === 0 && <div className="text-sm text-muted-foreground mt-4">No corporate entries yet. Performance fees are credited on client redemption.</div>}
    </div>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ email: "", name: "", role: "admin", password: "", expires_at: "" });
  const [issued, setIssued] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetMode, setResetMode] = useState("auto");
  const [customPw, setCustomPw] = useState("");
  const [resetExpiry, setResetExpiry] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [expiryTarget, setExpiryTarget] = useState(null);
  const [expiryVal, setExpiryVal] = useState("");
  const load = () => api.get("/super/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/super/users", {
        ...f,
        expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : null,
      });
      toast.success("User created.");
      setF({ email: "", name: "", role: "admin", password: "", expires_at: "" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const openReset = (u) => { setResetTarget(u); setResetMode("auto"); setCustomPw(""); setResetExpiry(""); };
  const submitReset = async (e) => {
    e.preventDefault();
    if (resetMode === "custom" && customPw.length < 8) return toast.error("Password must be at least 8 characters.");
    setResetBusy(true);
    try {
      const payload = {
        ...(resetMode === "custom" ? { new_password: customPw } : {}),
        ...(resetExpiry ? { expires_at: new Date(resetExpiry).toISOString() } : {}),
      };
      const r = await api.post(`/super/users/${resetTarget.id}/reset-password`, payload);
      setResetTarget(null); setCustomPw(""); setResetExpiry("");
      setIssued(r.data);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setResetBusy(false); }
  };
  const openExpiry = (u) => {
    setExpiryTarget(u);
    setExpiryVal(u.expires_at ? new Date(u.expires_at).toISOString().slice(0, 16) : "");
  };
  const submitExpiry = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/super/users/${expiryTarget.id}/expiry`, {
        expires_at: expiryVal ? new Date(expiryVal).toISOString() : null,
      });
      toast.success(expiryVal ? "Expiry updated." : "Expiry cleared.");
      setExpiryTarget(null); setExpiryVal("");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const isExpired = (u) => u.expires_at && new Date(u.expires_at) <= new Date();
  const delUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This wipes their holdings, transactions, requests, and e-signatures. Staff accounts are protected.`)) return;
    try {
      await api.delete(`/super/users/${u.id}`);
      toast.success(`Deleted ${u.email}.`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="grid md:grid-cols-3 gap-8">
      <div className="md:col-span-2">
        <h1 className="text-3xl mb-6">Users & Roles</h1>
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs uppercase tracking-widest"><tr><th className="text-left pb-3">Email</th><th className="text-left pb-3">Name</th><th className="text-left pb-3">Role</th><th className="text-left pb-3 pl-4">Created</th><th className="text-left pb-3 pl-4">Expires</th><th className="text-right pb-3">Actions</th></tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} className={`border-t border-border row-hover ${isExpired(u) ? "opacity-60" : ""}`}>
              <td className="py-3 font-mono text-xs">{u.email}</td>
              <td className="py-3">{u.name}</td>
              <td className="py-3 uppercase text-xs">{(u.role || "").replace("_", " ")}</td>
              <td className="py-3 pl-4 font-mono text-xs">{fmtDate(u.created_at)}</td>
              <td className="py-3 pl-4 font-mono text-xs" data-testid={`user-expiry-${u.id}`}>
                {u.expires_at ? (
                  <span className={isExpired(u) ? "neg" : "gold"}>
                    {fmtDateTime(u.expires_at)} {isExpired(u) ? "· EXPIRED" : ""}
                  </span>
                ) : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="py-3 text-right space-x-2">
                <button data-testid={`expiry-${u.id}`} onClick={() => openExpiry(u)}
                  className="text-xs uppercase tracking-widest border border-border hover:border-primary px-3 py-1">Set Expiry</button>
                <button data-testid={`pwreset-${u.id}`} onClick={() => openReset(u)}
                  className="text-xs uppercase tracking-widest border border-border hover:border-primary px-3 py-1">Reset Password</button>
                <button data-testid={`user-del-${u.id}`} onClick={() => delUser(u)}
                  className="text-xs uppercase tracking-widest border border-border hover:border-destructive px-3 py-1">Delete</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div>
        <h2 className="text-xl mb-4">New user</h2>
        <form onSubmit={submit} className="surface p-5 space-y-3">
          <F label="Email"><input required type="email" value={f.email} onChange={(e) => setF({...f, email: e.target.value})} className="fld" data-testid="user-email" /></F>
          <F label="Name"><input required value={f.name} onChange={(e) => setF({...f, name: e.target.value})} className="fld" data-testid="user-name" /></F>
          <F label="Role"><select value={f.role} onChange={(e) => setF({...f, role: e.target.value})} className="fld" data-testid="user-role">
            <option value="admin">Admin (Staff)</option>
            <option value="super_admin">Super Admin</option>
            <option value="client">Client</option>
            <option value="tester">Tester</option>
            <option value="potential_client">Potential Client</option>
          </select></F>
          <F label="Password"><input required type="password" minLength={8} value={f.password} onChange={(e) => setF({...f, password: e.target.value})} className="fld" data-testid="user-password" /></F>
          <F label="Access expires (optional)">
            <input type="datetime-local" value={f.expires_at}
                    onChange={(e) => setF({...f, expires_at: e.target.value})}
                    className="fld" data-testid="user-expires" />
          </F>
          <button data-testid="user-submit" className="btn-gold w-full py-2.5 text-xs uppercase tracking-widest">Create User</button>
          <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}`}</style>
        </form>
      </div>

      {resetTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <form onSubmit={submitReset} className="surface max-w-md w-full p-8" data-testid="pwreset-choose-modal">
            <div className="kicker mb-2">Reset Password</div>
            <h2 className="text-2xl mb-4">For {resetTarget.email}</h2>
            <p className="text-sm text-muted-foreground mb-5">Auto-generate a strong one-time password, or set a specific password you want to hand to this user.</p>
            <div className="flex gap-2 mb-5">
              {["auto", "custom"].map((m) => (
                <button type="button" key={m} onClick={() => setResetMode(m)}
                  data-testid={`pwreset-mode-${m}`}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border ${resetMode === m ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                  {m === "auto" ? "Auto-generate" : "Set custom"}
                </button>
              ))}
            </div>
            {resetMode === "custom" && (
              <label className="block mb-5"><span className="kicker text-[10px] block mb-2">New password (min 8 chars)</span>
                <input required minLength={8} type="text" value={customPw} onChange={(e) => setCustomPw(e.target.value)}
                  data-testid="pwreset-custom-input"
                  className="w-full bg-secondary border border-border px-3 py-2 text-sm font-mono"
                  placeholder="e.g. Piplight-2026!" autoFocus /></label>
            )}
            <label className="block mb-5">
              <span className="kicker text-[10px] block mb-2">Access expires (optional — leave blank to keep unchanged)</span>
              <input type="datetime-local" value={resetExpiry}
                onChange={(e) => setResetExpiry(e.target.value)}
                data-testid="pwreset-expiry"
                className="w-full bg-secondary border border-border px-3 py-2 text-sm font-mono" />
            </label>
            <div className="flex gap-2">
              <button data-testid="pwreset-confirm" disabled={resetBusy} className="btn-gold px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-50">{resetBusy ? "Working…" : "Confirm Reset"}</button>
              <button type="button" onClick={() => setResetTarget(null)} className="border border-border px-5 py-2 text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {expiryTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <form onSubmit={submitExpiry} className="surface max-w-md w-full p-8" data-testid="expiry-modal">
            <div className="kicker mb-2">Set Access Expiry</div>
            <h2 className="text-2xl mb-4">For {expiryTarget.email}</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Set a date/time after which this user can no longer sign in. Leave blank to grant permanent access. Useful for Testers, Potential Clients, or short-term reviewers.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                ["+1 day", 1], ["+7 days", 7], ["+30 days", 30], ["+90 days", 90],
              ].map(([label, days]) => (
                <button key={label} type="button"
                  onClick={() => {
                    const d = new Date(); d.setDate(d.getDate() + days);
                    setExpiryVal(d.toISOString().slice(0, 16));
                  }}
                  className="text-xs uppercase tracking-widest border border-border hover:border-primary px-3 py-1">
                  {label}
                </button>
              ))}
              <button type="button" onClick={() => setExpiryVal("")}
                className="text-xs uppercase tracking-widest border border-destructive/60 text-destructive px-3 py-1">
                Clear
              </button>
            </div>
            <label className="block mb-5">
              <span className="kicker text-[10px] block mb-2">Expires at</span>
              <input type="datetime-local" value={expiryVal}
                onChange={(e) => setExpiryVal(e.target.value)}
                data-testid="expiry-input"
                className="w-full bg-secondary border border-border px-3 py-2 text-sm font-mono" />
            </label>
            <div className="flex gap-2">
              <button data-testid="expiry-confirm" className="btn-gold px-5 py-2 text-xs uppercase tracking-widest">Save</button>
              <button type="button" onClick={() => setExpiryTarget(null)} className="border border-border px-5 py-2 text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {issued && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="surface max-w-md w-full p-8" data-testid="pwreset-modal">
            <div className="kicker mb-2">One-Time Display</div>
            <h2 className="text-2xl mb-4">New Password for {issued.email}</h2>
            <div className="text-sm text-muted-foreground mb-4">Deliver this securely. The user should change it after first login.</div>
            <div className="bg-secondary border border-primary p-4 font-mono text-lg break-all mb-6" data-testid="pwreset-value">{issued.temp_password}</div>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard.writeText(issued.temp_password); toast.success("Copied to clipboard."); }}
                className="btn-gold px-5 py-2 text-xs uppercase tracking-widest">Copy</button>
              <button onClick={() => setIssued(null)} className="border border-border px-5 py-2 text-xs uppercase tracking-widest">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Audit() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/super/audit-log").then((r) => setRows(r.data)); }, []);
  return (
    <div>
      <h1 className="text-3xl mb-6">Audit Log</h1>
      <table className="w-full text-sm">
        <thead className="text-muted-foreground text-xs uppercase tracking-widest"><tr><th className="text-left pb-3">Time</th><th className="text-left pb-3">Actor</th><th className="text-left pb-3">Action</th><th className="text-left pb-3">Entity</th><th className="text-left pb-3">Fund</th><th className="text-left pb-3">IP</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-t border-border">
            <td className="py-2 font-mono text-xs">{fmtDateTime(r.timestamp)}</td>
            <td className="py-2 font-mono text-xs">{r.actor_email}</td>
            <td className="py-2 uppercase text-xs">{r.action}</td>
            <td className="py-2 font-mono text-xs">{r.entity} · {r.entity_id?.slice(0, 8)}</td>
            <td className="py-2 font-mono text-xs">{r.fund_id || "—"}</td>
            <td className="py-2 font-mono text-xs">{r.ip || "—"}</td>
          </tr>
        ))}</tbody>
      </table>
      {rows.length === 0 && <div className="text-sm text-muted-foreground mt-4">Audit trail will populate as super-admin actions are performed.</div>}
    </div>
  );
}

function GateCodes() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ password: "", label: "", assigned_to: "", expires_at: "" });
  const [expTarget, setExpTarget] = useState(null);
  const [expVal, setExpVal] = useState("");
  const load = () => api.get("/super/gate-passwords").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/super/gate-passwords", {
        ...f,
        expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : null,
      });
      toast.success("Site code added.");
      setF({ password: "", label: "", assigned_to: "", expires_at: "" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete site code "${r.label}"? Anyone using ${r.password} will lose access.`)) return;
    try { await api.delete(`/super/gate-passwords/${r.id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const openExp = (r) => {
    setExpTarget(r);
    setExpVal(r.expires_at ? new Date(r.expires_at).toISOString().slice(0, 16) : "");
  };
  const submitExp = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/super/gate-passwords/${expTarget.id}/expiry`, {
        expires_at: expVal ? new Date(expVal).toISOString() : null,
      });
      toast.success(expVal ? "Expiry updated." : "Expiry cleared.");
      setExpTarget(null); setExpVal("");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const isExpired = (r) => r.expires_at && new Date(r.expires_at) <= new Date();
  return (
    <div className="grid md:grid-cols-3 gap-8">
      <div className="md:col-span-2">
        <h1 className="text-3xl mb-2">Site Access Codes</h1>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          These are the passwords the preview gate accepts. The <span className="gold">Senior Staff (master)</span> code is
          seeded as <span className="font-mono gold">PiPLighT2026</span> and can be edited or replaced. Additional
          per-user codes below can be handed to individual prospective clients — each one is independently revocable
          and can carry an automatic expiry date.
        </p>
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs uppercase tracking-widest"><tr><th className="text-left pb-3">Label</th><th className="text-left pb-3">Password</th><th className="text-left pb-3">Assigned To</th><th className="text-left pb-3">Expires</th><th className="text-right pb-3">Uses</th><th className="text-right pb-3">Actions</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className={`border-t border-border row-hover ${isExpired(r) ? "opacity-60" : ""}`}>
              <td className="py-3">{r.label}</td>
              <td className="py-3 font-mono text-xs gold">{r.password}</td>
              <td className="py-3 font-mono text-xs text-muted-foreground">{r.assigned_to || "—"}</td>
              <td className="py-3 font-mono text-xs" data-testid={`gate-expiry-${r.id}`}>
                {r.expires_at ? (
                  <span className={isExpired(r) ? "neg" : "gold"}>
                    {fmtDateTime(r.expires_at)} {isExpired(r) ? "· EXPIRED" : ""}
                  </span>
                ) : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="py-3 text-right font-mono">{r.use_count || 0}</td>
              <td className="py-3 text-right space-x-2">
                <button onClick={() => openExp(r)} data-testid={`gate-exp-${r.id}`}
                  className="text-xs uppercase tracking-widest border border-border hover:border-primary px-3 py-1">Set Expiry</button>
                <button onClick={() => del(r)} data-testid={`gate-del-${r.id}`}
                  className="text-xs uppercase tracking-widest border border-border hover:border-destructive px-3 py-1">Delete</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div>
        <h2 className="text-xl mb-4">Add code</h2>
        <form onSubmit={submit} className="surface p-5 space-y-3">
          <F label="Label (internal)"><input required value={f.label} onChange={(e) => setF({...f, label: e.target.value})} className="fld" data-testid="gate-label" /></F>
          <F label="Assigned to (optional)"><input value={f.assigned_to} onChange={(e) => setF({...f, assigned_to: e.target.value})} className="fld" data-testid="gate-assigned" /></F>
          <F label="Password (min 6 chars)"><input required minLength={6} type="text" value={f.password} onChange={(e) => setF({...f, password: e.target.value})} className="fld font-mono" data-testid="gate-pw" /></F>
          <F label="Auto-expire on (optional)"><input type="datetime-local" value={f.expires_at} onChange={(e) => setF({...f, expires_at: e.target.value})} className="fld" data-testid="gate-expires" /></F>
          <button data-testid="gate-add" className="btn-gold w-full py-2.5 text-xs uppercase tracking-widest">Add Site Code</button>
          <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}`}</style>
        </form>
      </div>

      {expTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <form onSubmit={submitExp} className="surface max-w-md w-full p-8" data-testid="gate-exp-modal">
            <div className="kicker mb-2">Auto-Expire Access Code</div>
            <h2 className="text-2xl mb-4">Code “{expTarget.label}”</h2>
            <p className="text-sm text-muted-foreground mb-5">
              The gate will automatically reject this code from the moment set below. Leave blank to remove the expiry and keep the code active until manually deleted.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[["+1 hour", 1/24], ["+1 day", 1], ["+7 days", 7], ["+30 days", 30]].map(([label, days]) => (
                <button key={label} type="button"
                  onClick={() => {
                    const d = new Date(); d.setTime(d.getTime() + days*86400000);
                    setExpVal(d.toISOString().slice(0, 16));
                  }}
                  className="text-xs uppercase tracking-widest border border-border hover:border-primary px-3 py-1">
                  {label}
                </button>
              ))}
              <button type="button" onClick={() => setExpVal("")}
                className="text-xs uppercase tracking-widest border border-destructive/60 text-destructive px-3 py-1">
                Clear
              </button>
            </div>
            <label className="block mb-5">
              <span className="kicker text-[10px] block mb-2">Expires at</span>
              <input type="datetime-local" value={expVal} onChange={(e) => setExpVal(e.target.value)}
                data-testid="gate-exp-input"
                className="w-full bg-secondary border border-border px-3 py-2 text-sm font-mono" />
            </label>
            <div className="flex gap-2">
              <button data-testid="gate-exp-confirm" className="btn-gold px-5 py-2 text-xs uppercase tracking-widest">Save</button>
              <button type="button" onClick={() => setExpTarget(null)} className="border border-border px-5 py-2 text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function fmtMetric(v, kind) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  if (kind === "pct") return `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;
  if (kind === "usd") return `${v < 0 ? "-" : ""}$${Math.abs(Number(v)).toLocaleString(undefined, {maximumFractionDigits: 2})}`;
  if (kind === "int") return Math.round(Number(v)).toLocaleString();
  return Number(v).toFixed(2);
}

// Same semantic tone rules as Home.jsx — kept in sync.
function metricTone(key, v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "";
  const n = Number(v);
  switch (key) {
    case "total_pnl":
    case "expectancy":
    case "gain_pct":
    case "avg_win":
      return n > 0 ? "pos" : n < 0 ? "neg" : "";
    case "avg_loss":
    case "commissions":
    case "max_drawdown_pct":
    case "lost_trades":
      return n !== 0 ? "neg" : "";
    case "won_trades":
      return n > 0 ? "pos" : "";
    case "win_rate_pct":
      return n >= 50 ? "pos" : n > 0 ? "neg" : "";
    case "profit_factor":
      return n > 1 ? "pos" : n > 0 ? "neg" : "";
    case "sharpe":
    case "sortino":
    case "calmar":
      return n >= 1 ? "pos" : n < 0 ? "neg" : "";
    default:
      return "";
  }
}

function MT5Desk() {
  const FUND_TABS = [
    { id: "fx-multi", label: "FXA · FX Multi-Asset" },
    { id: "fx-metals", label: "FXM · FX Metals Focus" },
  ];
  const [fundId, setFundId] = useState("fx-multi");
  const [status, setStatus] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [labels, setLabels] = useState([]);
  const [trades, setTrades] = useState([]);
  const [creds, setCreds] = useState({ server: "", login: "", password: "" });
  const [busy, setBusy] = useState("");
  const load = () => Promise.all([
    api.get(`/super/mt5/status?fund_id=${fundId}`).then((r) => {
      setStatus(r.data);
      setCreds({ server: r.data.server || "", login: r.data.login || "", password: "" });
    }),
    api.get(`/mt5/metrics?fund_id=${fundId}`).then((r) => { setMetrics(r.data.metrics); setLabels(r.data.labels); }),
    api.get(`/super/mt5/trades?fund_id=${fundId}`).then((r) => setTrades(r.data)),
  ]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fundId]);
  const saveCreds = async (e) => {
    e.preventDefault();
    if (!creds.server || !creds.login) { toast.error("Broker server and account are required."); return; }
    setBusy("creds");
    try {
      await api.put("/super/mt5/credentials", { ...creds, fund_id: fundId });
      toast.success("Credentials saved.");
      setCreds({ ...creds, password: "" });
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(""); }
  };
  return (
    <div>
      <h1 className="text-3xl mb-2">Trading Desk · MT5 (Webhook)</h1>
      <p className="text-sm text-muted-foreground mb-4 max-w-3xl">
        Trades stream in from the <span className="gold">PiplightWebhook</span> Expert Advisor on each fund's MT5 terminal. Each fund has its own account, blotter, and metric grid. Switch funds with the tabs below.
      </p>
      <div className="flex gap-2 mb-6 border-b border-border" data-testid="mt5-fund-tabs">
        {FUND_TABS.map((t) => (
          <button key={t.id} onClick={() => setFundId(t.id)}
            data-testid={`mt5-tab-${t.id}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px ${fundId === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {status && (
        <div className="surface p-5 mb-6" data-testid="mt5-status-card">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div className="kicker">Bridge Status · <span className="gold">Webhook</span></div>
            {status.last_webhook && (
              <div className="text-xs text-muted-foreground font-mono">
                last post: {fmtDateTime(status.last_webhook.at)} · ticket {status.last_webhook.ticket}
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div><span className="kicker text-[10px] block">Fund</span><span className="font-mono">{status.fund_code} · {status.fund_name}</span></div>
            <div><span className="kicker text-[10px] block">Trades ingested</span><span className="font-mono">{status.trades_ingested}</span></div>
            <div><span className="kicker text-[10px] block">MT5 accounts posting</span><span className="font-mono">{(status.accounts_seen || []).join(", ") || "—"}</span></div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">{status.note}</p>
        </div>
      )}

      <form onSubmit={saveCreds} className="surface p-5 mb-6" data-testid="mt5-creds-form">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div className="kicker">MT5 Credentials Vault · reference only</div>
          <div className="text-xs text-muted-foreground">
            {status?.password_present ? "Password on file" : "No password saved"}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-4 max-w-3xl">
          Stored securely in Mongo for your own bookkeeping — the platform never uses these values to log into MT5 or MetaAPI. Trades reach the app only through the on-terminal PiplightWebhook EA.
        </p>
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <label className="block">
            <span className="kicker text-[10px] block mb-2">Broker Server</span>
            <input data-testid="mt5-creds-server" value={creds.server}
              onChange={(e) => setCreds({ ...creds, server: e.target.value })}
              className="w-full bg-transparent border border-border px-3 py-2 text-sm font-mono"
              placeholder="ICMarketsSC-Demo" required />
          </label>
          <label className="block">
            <span className="kicker text-[10px] block mb-2">Account (Login)</span>
            <input data-testid="mt5-creds-login" value={creds.login}
              onChange={(e) => setCreds({ ...creds, login: e.target.value })}
              className="w-full bg-transparent border border-border px-3 py-2 text-sm font-mono"
              placeholder="53034302" required />
          </label>
          <label className="block">
            <span className="kicker text-[10px] block mb-2">Password</span>
            <input data-testid="mt5-creds-password" type="password" value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              className="w-full bg-transparent border border-border px-3 py-2 text-sm font-mono"
              placeholder={status?.password_present ? "•••••••••• (leave blank to keep)" : "MT5 investor password"}
              autoComplete="new-password" />
          </label>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground max-w-xl">
            Leave the password field blank if you only want to update the server or login.
          </p>
          <button data-testid="mt5-creds-save" disabled={!!busy} type="submit"
            className="btn-gold px-6 py-2 text-xs uppercase tracking-widest disabled:opacity-50 shrink-0">
            {busy === "creds" ? "Saving…" : "Save Credentials"}
          </button>
        </div>
      </form>

      <div className="flex items-baseline justify-between mb-3">
        <div className="kicker">16 Institutional Metrics</div>
        <div className="text-xs font-mono text-muted-foreground">source: <span className="gold">{metrics?.source || "—"}</span></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {(labels || []).map(([label, key, kind]) => {
          const v = metrics?.[key];
          const tone = metricTone(key, v);
          return (
            <div key={key} className="stat-tile" data-testid={`mt5-${key}`}>
              <div className="kicker text-[10px] mb-1">{label}</div>
              <div className={`font-mono text-lg ${tone}`}>{fmtMetric(v, kind)}</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mb-3">
        <div className="kicker">Trade Blotter (Super Admin only)</div>
        <a data-testid="mt5-nav-recon-btn"
           href={`${(api.defaults?.baseURL || "").replace(/\/$/,"")}/super/nav-reconciliation.pdf`}
           target="_blank" rel="noopener"
           className="text-xs uppercase tracking-widest border border-border hover:border-primary px-3 py-2">
          Download NAV Reconciliation PDF
        </a>
      </div>
      {trades.length === 0 ? (
        <div className="text-sm text-muted-foreground">No trades yet. Once a deal closes on the MT5 terminal, the PiplightWebhook EA will forward it here within a second.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs uppercase tracking-widest">
            <tr><th className="text-left pb-3">Ticket</th><th className="text-left pb-3">Symbol</th><th className="text-left pb-3">Side</th><th className="text-right pb-3">Volume</th><th className="text-right pb-3">Commission</th><th className="text-right pb-3">P/L</th><th className="text-left pb-3 pl-4">Closed</th></tr>
          </thead>
          <tbody>{trades.map((t) => (
            <tr key={t.ticket} className="border-t border-border">
              <td className="py-2 font-mono text-xs">{t.ticket}</td>
              <td className="py-2 font-mono">{t.symbol || "—"}</td>
              <td className="py-2 uppercase text-xs">{t.side}</td>
              <td className="py-2 text-right font-mono">{t.volume ?? "—"}</td>
              <td className="py-2 text-right font-mono text-muted-foreground">{t.commission != null ? fmtUSD(t.commission) : "—"}</td>
              <td className={`py-2 text-right font-mono ${t.profit >= 0 ? "pos" : "neg"}`}>{fmtUSD(t.profit)}</td>
              <td className="py-2 pl-4 font-mono text-xs">{t.close_time ? fmtDateTime(t.close_time) : "—"}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

function Referrals() {
  const [data, setData] = useState({ rows: [], total_accrued_unpaid: 0, total_paid_lifetime: 0 });
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ referrer_user_id: "", referee_user_id: "", notes: "" });
  const [payTarget, setPayTarget] = useState(null);
  const [pay, setPay] = useState({ amount_paid: "", payment_method: "wire", payment_reference: "", notes: "" });
  const load = () => Promise.all([
    api.get("/super/referrals").then((r) => setData(r.data)),
    api.get("/super/users").then((r) => setUsers(r.data)),
  ]);
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    if (!f.referrer_user_id || !f.referee_user_id) return toast.error("Select both a referrer and a referee.");
    try {
      await api.post("/super/referrals", f);
      toast.success("Referral link created.");
      setF({ referrer_user_id: "", referee_user_id: "", notes: "" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const openPay = (r) => {
    setPayTarget(r);
    setPay({ amount_paid: r.accrued_commission_15pct || "", payment_method: "wire", payment_reference: "", notes: "" });
  };
  const submitPay = async (e) => {
    e.preventDefault();
    if (!pay.payment_reference) return toast.error("Enter a payment reference (wire / cheque / transfer number).");
    try {
      await api.post(`/super/referrals/${payTarget.id}/pay`, {
        ...pay, amount_paid: parseFloat(pay.amount_paid) });
      toast.success(`Marked paid to ${payTarget.referrer?.email}.`);
      setPayTarget(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Remove referral link ${r.referrer?.email} → ${r.referee?.email}? Only do this if it was created in error.`)) return;
    try { await api.delete(`/super/referrals/${r.id}`); toast.success("Removed."); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const badge = (row) => {
    if (row.status === "paid") return <span className="text-xs px-2 py-0.5 border border-primary text-primary uppercase tracking-widest">Paid</span>;
    if (row.ready_to_pay) return <span className="text-xs px-2 py-0.5 border border-green-500 text-green-500 uppercase tracking-widest">Ready</span>;
    if (row.year_1_locked) return <span className="text-xs px-2 py-0.5 border border-muted-foreground text-muted-foreground uppercase tracking-widest">Y1 closed</span>;
    return <span className="text-xs px-2 py-0.5 border border-border text-muted-foreground uppercase tracking-widest">Accruing</span>;
  };
  return (
    <div>
      <h1 className="text-3xl mb-2">Referral Program</h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-3xl leading-relaxed">
        Referrers earn <span className="gold">15% of the profit generated by their referee during the first 12 months</span> from the referee's fund inception date. The commission is paid once, at the end of that year. Numbers below are computed live from each referee's holdings — accrued commissions flip to <span className="text-green-500">Ready</span> the moment the year-1 window closes.
      </p>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="stat-tile">
          <div className="kicker text-[10px] mb-2">Total Accrued · Unpaid</div>
          <div className="font-mono text-2xl gold">{fmtUSD(data.total_accrued_unpaid)}</div>
        </div>
        <div className="stat-tile">
          <div className="kicker text-[10px] mb-2">Total Paid · Lifetime</div>
          <div className="font-mono text-2xl">{fmtUSD(data.total_paid_lifetime)}</div>
        </div>
        <div className="stat-tile">
          <div className="kicker text-[10px] mb-2">Active Links</div>
          <div className="font-mono text-2xl">{data.rows.filter((r) => r.status !== "paid").length}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left pb-3">Referrer → Referee</th>
                <th className="text-right pb-3">Referee Profit</th>
                <th className="text-right pb-3">Accrued 15%</th>
                <th className="text-left pb-3 pl-4">Year-1 Ends</th>
                <th className="text-right pb-3">Status</th>
                <th className="text-right pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-t border-border row-hover" data-testid={`ref-row-${r.id}`}>
                  <td className="py-3">
                    <div className="text-xs font-mono">{r.referrer?.email || "—"}</div>
                    <div className="text-xs text-muted-foreground font-mono">→ {r.referee?.email || "—"}</div>
                  </td>
                  <td className={`py-3 text-right font-mono ${r.referee_profit_ytd >= 0 ? "pos" : "neg"}`}>
                    {fmtUSD(r.referee_profit_ytd)}
                  </td>
                  <td className="py-3 text-right font-mono gold">
                    {fmtUSD(r.accrued_commission_15pct)}
                    {r.status === "paid" && (
                      <div className="text-[10px] text-muted-foreground normal-case tracking-normal">paid {fmtUSD(r.amount_paid)}</div>
                    )}
                  </td>
                  <td className="py-3 pl-4 font-mono text-xs">
                    {r.year_end_date ? (
                      <>
                        {fmtDate(r.year_end_date)}
                        {r.days_remaining_in_y1 !== null && !r.year_1_locked && (
                          <div className="text-[10px] text-muted-foreground">{r.days_remaining_in_y1} days left</div>
                        )}
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-3 text-right">{badge(r)}</td>
                  <td className="py-3 text-right space-x-2">
                    {r.status !== "paid" && (
                      <button data-testid={`ref-pay-${r.id}`} onClick={() => openPay(r)}
                        className={`text-xs uppercase tracking-widest border px-3 py-1 ${r.ready_to_pay ? "border-primary text-primary hover:bg-primary hover:text-primary-foreground" : "border-border hover:border-primary"}`}>
                        Mark Paid
                      </button>
                    )}
                    <button data-testid={`ref-del-${r.id}`} onClick={() => del(r)}
                      className="text-xs uppercase tracking-widest border border-border hover:border-destructive px-3 py-1">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.rows.length === 0 && (
            <div className="text-sm text-muted-foreground mt-6">
              No referrals yet. Link a referrer to a referee on the right to begin tracking their 15% commission automatically.
            </div>
          )}
        </div>

        <div>
          <h2 className="text-xl mb-4">Link a referral</h2>
          <form onSubmit={create} className="surface p-5 space-y-3">
            <F label="Referrer">
              <select required value={f.referrer_user_id}
                onChange={(e) => setF({ ...f, referrer_user_id: e.target.value })}
                className="fld" data-testid="ref-referrer">
                <option value="">— Select referrer —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
                ))}
              </select>
            </F>
            <F label="Referee (new client they brought in)">
              <select required value={f.referee_user_id}
                onChange={(e) => setF({ ...f, referee_user_id: e.target.value })}
                className="fld" data-testid="ref-referee">
                <option value="">— Select referee —</option>
                {users.filter((u) => u.id !== f.referrer_user_id).map((u) => (
                  <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
                ))}
              </select>
            </F>
            <F label="Notes (optional)">
              <textarea rows={2} value={f.notes}
                onChange={(e) => setF({ ...f, notes: e.target.value })}
                className="fld" data-testid="ref-notes"
                placeholder="e.g. Introduced at London roadshow, Feb 2026" />
            </F>
            <button data-testid="ref-submit" className="btn-gold w-full py-2.5 text-xs uppercase tracking-widest">Create Referral</button>
            <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}`}</style>
          </form>
        </div>
      </div>

      {payTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <form onSubmit={submitPay} className="surface max-w-md w-full p-8" data-testid="ref-pay-modal">
            <div className="kicker mb-2">Mark Referral Paid</div>
            <h2 className="text-2xl mb-2">To {payTarget.referrer?.email}</h2>
            <p className="text-xs text-muted-foreground mb-5 font-mono">
              For referring {payTarget.referee?.email} · Accrued 15% = {fmtUSD(payTarget.accrued_commission_15pct)}
            </p>
            <label className="block mb-4">
              <span className="kicker text-[10px] block mb-2">Amount paid (USD)</span>
              <input type="number" step="0.01" required value={pay.amount_paid}
                onChange={(e) => setPay({ ...pay, amount_paid: e.target.value })}
                data-testid="ref-pay-amount"
                className="w-full bg-secondary border border-border px-3 py-2 text-sm font-mono" />
            </label>
            <label className="block mb-4">
              <span className="kicker text-[10px] block mb-2">Method</span>
              <select value={pay.payment_method}
                onChange={(e) => setPay({ ...pay, payment_method: e.target.value })}
                data-testid="ref-pay-method"
                className="w-full bg-secondary border border-border px-3 py-2 text-sm font-mono">
                <option value="wire">Wire</option>
                <option value="internal_credit">Internal credit (added to their fund holdings)</option>
                <option value="check">Cheque</option>
                <option value="ach">ACH</option>
                <option value="crypto">Crypto</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="block mb-4">
              <span className="kicker text-[10px] block mb-2">Reference (wire / cheque / txn ID)</span>
              <input required value={pay.payment_reference}
                onChange={(e) => setPay({ ...pay, payment_reference: e.target.value })}
                data-testid="ref-pay-ref"
                className="w-full bg-secondary border border-border px-3 py-2 text-sm font-mono" />
            </label>
            <label className="block mb-6">
              <span className="kicker text-[10px] block mb-2">Notes (optional)</span>
              <textarea rows={2} value={pay.notes}
                onChange={(e) => setPay({ ...pay, notes: e.target.value })}
                data-testid="ref-pay-notes"
                className="w-full bg-secondary border border-border px-3 py-2 text-sm font-mono" />
            </label>
            <div className="flex gap-2">
              <button data-testid="ref-pay-confirm" className="btn-gold px-5 py-2 text-xs uppercase tracking-widest">Confirm Payment</button>
              <button type="button" onClick={() => setPayTarget(null)}
                className="border border-border px-5 py-2 text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Preview Access Requests — inbound prospects from the gate
// ------------------------------------------------------------
function AccessRequests() {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({ hours_valid: 72, admin_note: "" });
  const load = () => api.get("/super/gate-access-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    try {
      const r = await api.post(`/super/gate-access-requests/${id}/approve`, form);
      toast.success(`Approved · code ${r.data.code} · expires ${fmtDateTime(r.data.expires_at)}`);
      setOpenId(null); setForm({ hours_valid: 72, admin_note: "" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const reject = async (id) => {
    if (!window.confirm("Reject this access request?")) return;
    try { await api.post(`/super/gate-access-requests/${id}/reject`); toast.success("Rejected"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const pending = rows.filter((r) => r.status === "pending");
  const closed = rows.filter((r) => r.status !== "pending");
  return (
    <div>
      <div className="mb-6">
        <div className="kicker mb-1">Prospect Inbox</div>
        <h1 className="text-3xl">Preview Access Requests</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Visitors who reach the preview gate without a code can leave their email here. Approve to auto-generate a time-limited gate code and email it to them.
        </p>
      </div>

      <div className="mb-8">
        <div className="kicker mb-3">Pending · {pending.length}</div>
        {pending.length === 0 && <div className="text-sm text-muted-foreground border border-border p-4">No pending requests.</div>}
        <div className="space-y-3">
          {pending.map((r) => (
            <div key={r.id} className="surface p-5" data-testid={`gar-row-${r.id}`}>
              <div className="flex justify-between mb-2 flex-wrap gap-2">
                <div>
                  <div className="text-base">{r.name || <span className="text-muted-foreground">(no name)</span>}</div>
                  <div className="font-mono text-xs text-muted-foreground">{r.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest gold">pending</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{fmtDateTime(r.requested_at)}</div>
                </div>
              </div>
              {r.note && <div className="text-xs text-muted-foreground mb-3 italic border-l-2 border-border pl-3">"{r.note}"</div>}
              <div className="font-mono text-[10px] text-muted-foreground mb-3">IP {r.ip || "—"} · UA {(r.ua || "").slice(0, 60)}…</div>
              {openId === r.id ? (
                <div className="border border-primary/40 bg-primary/5 p-4">
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-xs text-muted-foreground uppercase tracking-widest">Code valid for (hours)</span>
                      <input type="number" min="1" max="2160" value={form.hours_valid}
                        onChange={(e) => setForm({ ...form, hours_valid: Number(e.target.value) })}
                        data-testid={`gar-hours-${r.id}`}
                        className="mt-1 w-full bg-secondary/40 border border-border p-2 text-sm font-mono" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-muted-foreground uppercase tracking-widest">Note in email (optional)</span>
                      <input value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })}
                        data-testid={`gar-note-${r.id}`}
                        placeholder="e.g. Welcome — feel free to book a call after browsing."
                        className="mt-1 w-full bg-secondary/40 border border-border p-2 text-sm" />
                    </label>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => approve(r.id)} data-testid={`gar-approve-submit-${r.id}`}
                      className="btn-gold px-4 py-2 text-xs uppercase tracking-widest">Generate & Email Code</button>
                    <button onClick={() => setOpenId(null)}
                      className="border border-border px-4 py-2 text-xs uppercase tracking-widest">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setOpenId(r.id)} data-testid={`gar-approve-${r.id}`}
                    className="btn-gold px-4 py-2 text-xs uppercase tracking-widest">Approve</button>
                  <button onClick={() => reject(r.id)} data-testid={`gar-reject-${r.id}`}
                    className="border border-border hover:border-destructive px-4 py-2 text-xs uppercase tracking-widest">Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="kicker mb-3">Recently processed</div>
        {closed.length === 0 && <div className="text-sm text-muted-foreground">Nothing yet.</div>}
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-xs uppercase tracking-widest">
            <tr><th className="text-left pb-3">Email</th><th className="text-left pb-3">Status</th><th className="text-left pb-3">Code</th><th className="text-left pb-3">Expires</th><th className="text-left pb-3">By</th></tr>
          </thead>
          <tbody>
            {closed.map((r) => (
              <tr key={r.id} className="border-t border-border row-hover" data-testid={`gar-closed-${r.id}`}>
                <td className="py-3 font-mono text-xs">{r.email}</td>
                <td className={`py-3 text-xs uppercase tracking-widest ${r.status === "approved" ? "pos" : "neg"}`}>{r.status}</td>
                <td className="py-3 font-mono text-xs">{r.granted_code || "—"}</td>
                <td className="py-3 font-mono text-xs">{r.code_expires_at ? fmtDateTime(r.code_expires_at) : "—"}</td>
                <td className="py-3 font-mono text-xs text-muted-foreground">{r.granted_by || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ------------------------------------------------------------
// Onboard / Deposit — record a new client wire and credit units
// ------------------------------------------------------------
function OnboardDeposit() {
  const [funds, setFunds] = useState([]);
  const [clients, setClients] = useState([]);
  const [apps, setApps] = useState([]);
  const [f, setF] = useState({
    client_email: "", client_name: "", fund_id: "",
    deposit_usd: "", subscription_date: new Date().toISOString().slice(0, 10),
    note: "",
  });
  const [preview, setPreview] = useState(null); // {nav, units}
  const [result, setResult] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/funds").then((r) => r.data),
      api.get("/admin/clients").then((r) => r.data).catch(() => []),
      api.get("/admin/applications").then((r) => r.data).catch(() => []),
    ]).then(([fs, cs, as]) => {
      setFunds(fs);
      setClients(cs);
      setApps(as);
      if (!f.fund_id && fs[0]) setF((x) => ({ ...x, fund_id: fs[0].id }));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Preview units = deposit / current NAV
  useEffect(() => {
    const dep = Number(f.deposit_usd);
    if (!f.fund_id || !dep || dep <= 0) { setPreview(null); return; }
    api.get(`/funds/${f.fund_id}/nav-history?limit=1`).then((r) => {
      const latest = (r.data || []).slice(-1)[0];
      const nav = latest?.nav_per_unit;
      if (nav && nav > 0) setPreview({ nav, units: dep / nav });
      else setPreview(null);
    }).catch(() => setPreview(null));
  }, [f.fund_id, f.deposit_usd]);

  const pickClient = (c) => setF((x) => ({ ...x, client_email: c.email, client_name: c.name }));
  const pickApplicant = (a) => setF((x) => ({ ...x, client_email: a.email, client_name: a.full_name,
                                              fund_id: (a.fund_ids || [])[0] || x.fund_id }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/super/onboard-deposit", {
        ...f, deposit_usd: Number(f.deposit_usd),
      });
      setResult(r.data);
      toast.success(`${r.data.action === "topup" ? "Top-up" : "New holding"} recorded · ${fmtNum(r.data.units_credited, 4)} units at NAV ${fmtNum(r.data.nav_used, 4)}`);
      setF((x) => ({ ...x, deposit_usd: "", note: "" }));
    } catch (er) { toast.error(formatErr(er)); }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <div className="kicker mb-1">Client Money-In</div>
        <h1 className="text-4xl">Onboard / Deposit</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Record a wire that has hit the operating account and been forwarded to the brokerage.
          The system converts the USD amount into fund units at the <em>current NAV</em>, credits
          the client's holding (or tops it up), and posts a subscription ledger entry the client
          will see immediately on their dashboard.
        </p>
      </div>

      <form onSubmit={submit} className="surface p-6 grid md:grid-cols-2 gap-4" data-testid="onboard-form">
        <F label="Client email">
          <input required type="email" value={f.client_email}
            onChange={(e) => setF({ ...f, client_email: e.target.value })}
            className="fld" data-testid="onb-email" />
        </F>
        <F label="Client name (only needed for brand-new clients)">
          <input value={f.client_name}
            onChange={(e) => setF({ ...f, client_name: e.target.value })}
            className="fld" data-testid="onb-name" />
        </F>
        <F label="Fund">
          <select required value={f.fund_id}
            onChange={(e) => setF({ ...f, fund_id: e.target.value })}
            className="fld" data-testid="onb-fund">
            {funds.map((x) => <option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}
          </select>
        </F>
        <F label="Deposit (USD received & wired to brokerage)">
          <input required type="number" step="0.01" min="0" value={f.deposit_usd}
            onChange={(e) => setF({ ...f, deposit_usd: e.target.value })}
            className="fld font-mono" data-testid="onb-deposit" />
        </F>
        <F label="Effective date (wire settled)">
          <input required type="date" value={f.subscription_date}
            onChange={(e) => setF({ ...f, subscription_date: e.target.value })}
            className="fld font-mono" data-testid="onb-date" />
        </F>
        <F label="Wire reference / note (optional)">
          <input value={f.note}
            onChange={(e) => setF({ ...f, note: e.target.value })}
            className="fld" placeholder="e.g. wire ref FED-2026-091234" data-testid="onb-note" />
        </F>

        {preview && (
          <div className="md:col-span-2 border border-primary/40 bg-primary/5 p-4 text-sm" data-testid="onb-preview">
            <div className="kicker text-[10px] mb-2 gold">Preview</div>
            <div className="grid grid-cols-3 gap-4 font-mono">
              <div><div className="text-muted-foreground text-xs">Current NAV</div><div>{fmtNum(preview.nav, 4)}</div></div>
              <div><div className="text-muted-foreground text-xs">Units credited</div><div>{fmtNum(preview.units, 4)}</div></div>
              <div><div className="text-muted-foreground text-xs">Deposit</div><div>{fmtUSD(Number(f.deposit_usd))}</div></div>
            </div>
          </div>
        )}

        <div className="md:col-span-2">
          <button type="submit" data-testid="onb-submit"
            className="btn-gold px-6 py-2.5 text-xs uppercase tracking-widest">
            Record Deposit &amp; Credit Units
          </button>
        </div>
        <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}`}</style>
      </form>

      {result && (
        <div className="mt-6 border border-primary/40 bg-card/40 p-5" data-testid="onb-result">
          <div className="kicker text-[10px] mb-2 gold">
            {result.action === "topup" ? "Top-up recorded" : "New holding created"}
          </div>
          <div className="grid md:grid-cols-3 gap-4 text-sm font-mono">
            <div><div className="text-muted-foreground text-xs">NAV used</div><div>{fmtNum(result.nav_used, 4)}</div></div>
            <div><div className="text-muted-foreground text-xs">Units credited</div><div>{fmtNum(result.units_credited, 4)}</div></div>
            <div><div className="text-muted-foreground text-xs">Client ID</div><div className="text-xs">{result.client_id}</div></div>
          </div>
          {result.temp_password && (
            <div className="mt-4 border border-primary/40 bg-primary/5 p-3 text-xs">
              <div className="kicker text-[10px] mb-1 gold">Temporary Password (share with client)</div>
              <code className="font-mono text-sm">{result.temp_password}</code>
              <button type="button" onClick={() => { navigator.clipboard.writeText(result.temp_password); toast.success("Copied"); }}
                className="ml-3 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">Copy</button>
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <div className="border border-border p-5">
          <div className="kicker mb-3">Approved applicants (click to pre-fill)</div>
          {apps.filter((a) => a.status === "approved").length === 0 && (
            <div className="text-xs text-muted-foreground">No approved applicants yet. Approve applications first, then wire.</div>
          )}
          <ul className="text-sm space-y-1">
            {apps.filter((a) => a.status === "approved").map((a) => (
              <li key={a.id}>
                <button onClick={() => pickApplicant(a)}
                  className="text-left hover:text-primary" data-testid={`onb-pick-app-${a.id}`}>
                  {a.full_name} · <span className="font-mono text-xs">{a.email}</span> · <span className="text-xs text-muted-foreground">{(a.fund_ids || []).join(", ")}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-border p-5">
          <div className="kicker mb-3">Existing clients (click to pre-fill for top-up)</div>
          {clients.length === 0 && (
            <div className="text-xs text-muted-foreground">No clients on record yet.</div>
          )}
          <ul className="text-sm space-y-1">
            {clients.map((c) => (
              <li key={c.id}>
                <button onClick={() => pickClient(c)}
                  className="text-left hover:text-primary" data-testid={`onb-pick-cli-${c.id}`}>
                  {c.name} · <span className="font-mono text-xs">{c.email}</span> · <span className="text-xs text-muted-foreground">{c.holdings?.length || 0} holding(s)</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}


export default function SuperPortal() {
  return (
    <Routes>
      <Route index element={<Metrics />} />
      <Route path="applications" element={<AdminApplications />} />
      <Route path="access-requests" element={<AccessRequests />} />
      <Route path="onboard" element={<OnboardDeposit />} />
      <Route path="clients" element={<AdminClients />} />
      <Route path="nav" element={<NAVEntry />} />
      <Route path="fees" element={<Fees />} />
      <Route path="corporate" element={<Corporate />} />
      <Route path="users" element={<Users />} />
      <Route path="gate" element={<GateCodes />} />
      <Route path="referrals" element={<Referrals />} />
      <Route path="mt5" element={<MT5Desk />} />
      <Route path="audit" element={<Audit />} />
    </Routes>
  );
}
